import React, { useEffect, useState } from 'react';
import { Bot, GitBranch, Users, MessageSquare, TrendingUp } from 'lucide-react';

// Casa Libre "solar system": the Vetobase logo at the centre with all the
// icons that orbited the old 3D globe — the 5 integration logos plus the 5
// product feature icons — revolving on concentric rings.
//
// Icons are positioned by maths every frame (cos/sin on a circle), NOT by CSS
// rotate(), so they only revolve around the logo and never tilt or spin on
// their own axis. The rings themselves are static dashed circles.

// Every icon that was in the globe. `img` = a logo file, `Icon` = a lucide glyph.
const RINGS = [
  // inner ring: product features
  {
    size: 50, dur: 34, dir: 1, offset: -90,
    items: [
      { Icon: Bot, label: 'AI Bot' },
      { Icon: GitBranch, label: 'Workflows' },
      { Icon: Users, label: 'CRM' },
      { Icon: MessageSquare, label: 'Live Chat' },
      { Icon: TrendingUp, label: 'Analytics' },
    ],
  },
  // outer ring: social / integration platforms
  {
    size: 90, dur: 56, dir: -1, offset: -90,
    items: [
      { img: '/wa.jpg', label: 'WhatsApp' },
      { img: '/instagram.jpg', label: 'Instagram' },
      { img: '/shopify.jpg', label: 'Shopify' },
      { img: '/facebook.jpg', label: 'Facebook' },
      { img: '/postex.webp', label: 'PostEx' },
    ],
  },
];

const Badge = ({ item }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
    <div
      style={{
        width: 'clamp(36px, 10vw, 48px)', maxWidth: 48, aspectRatio: '1',
        borderRadius: '50%', background: '#fff', border: '1px solid #E7E1D6',
        boxShadow: '4px 4px 0 rgba(17,17,17,.12)', display: 'grid', placeItems: 'center',
        overflow: 'hidden',
      }}
    >
      {item.img
        ? <img src={item.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
        : <item.Icon strokeWidth={2} style={{ width: '52%', height: '52%', color: '#111111' }} />}
    </div>
    <span style={{
      fontFamily: '"IBM Plex Mono", ui-monospace, monospace', fontSize: 9, fontWeight: 600,
      letterSpacing: '0.12em', textTransform: 'uppercase', color: '#5B5750',
      whiteSpace: 'nowrap', lineHeight: 1,
    }}>
      {item.label}
    </span>
  </div>
);

// Seconds elapsed since mount, ticking every animation frame.
// Honours prefers-reduced-motion by staying at 0 (static layout).
function useClock() {
  const [t, setT] = useState(0);
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    let raf;
    const start = performance.now();
    const tick = (now) => { setT((now - start) / 1000); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return t;
}

const SolarSystem = () => {
  const t = useClock();

  return (
    <div style={{ position: 'relative', width: '100%', aspectRatio: '1', maxWidth: 520, margin: '0 auto' }}>
      {/* faint dotted field behind the system */}
      <div style={{
        position: 'absolute', inset: '6%', borderRadius: '50%',
        backgroundImage: 'radial-gradient(rgba(17,17,17,.05) 1px, transparent 1px)', backgroundSize: '18px 18px',
        pointerEvents: 'none',
      }} />

      {/* static orbit rings */}
      {RINGS.map((r, i) => (
        <div key={`ring-${i}`} style={{
          position: 'absolute', top: '50%', left: '50%', width: `${r.size}%`, height: `${r.size}%`,
          transform: 'translate(-50%, -50%)', borderRadius: '50%',
          border: '1.5px dashed rgba(17,17,17,.34)', pointerEvents: 'none',
        }} />
      ))}

      {/* orbiting icons — each placed on its ring by angle, always upright */}
      {RINGS.map((r, ri) => {
        const radius = r.size / 2; // % of container
        const base = (t / r.dur) * 360 * r.dir + r.offset;
        return r.items.map((item, i) => {
          const deg = base + (360 / r.items.length) * i;
          const rad = (deg * Math.PI) / 180;
          const left = 50 + radius * Math.cos(rad);
          const top = 50 + radius * Math.sin(rad);
          return (
            <div
              key={`${ri}-${i}`}
              style={{
                position: 'absolute', left: `${left}%`, top: `${top}%`,
                // anchor on the icon's centre (not the icon+label column) so the icon sits on the ring
                transform: 'translate(-50%, -24px)', zIndex: 1, willChange: 'left, top',
              }}
            >
              <Badge item={item} />
            </div>
          );
        });
      })}

      {/* centre: Vetobase logo — same white tile treatment as the sidebar / sign-in page
          (white card, thin stroke, hard offset shadow) — not a black box */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 2,
      }}>
        <div style={{
          width: 'clamp(72px, 19vw, 104px)', maxWidth: 104, aspectRatio: '1', borderRadius: '50%',
          background: '#FFFFFF', border: '1px solid #E7E1D6', display: 'grid', placeItems: 'center',
          boxShadow: '5px 5px 0 rgba(17,17,17,.14)', animation: 'cl-float 5s ease-in-out infinite',
        }}>
          <img src="/new_logo.png" alt="Vetobase AI" style={{ width: '58%', height: '58%', objectFit: 'contain', borderRadius: 12 }} />
        </div>
      </div>
    </div>
  );
};

export default SolarSystem;
